import { Button, Input, type InputRef, Modal, Select } from "antd"
import { useEffect, useRef, useState } from "react"

import Flex from "#blocks/Flex"

import {
  CreatePortfolioProjectInput,
  PortfolioMockProject,
} from "./usePortfolioDbStorage"

interface PortfolioProjectModalProps {
  open: boolean
  onClose: () => void
  onCreateProject: (input: CreatePortfolioProjectInput) => void
  initialCategory?: PortfolioMockProject["category"]
}

const categoryOptions: Array<{
  label: string
  value: PortfolioMockProject["category"]
}> = [
  { label: "Selected Work", value: "selected" },
  { label: "Sustained Investigation", value: "sustained" },
  { label: "Paused", value: "paused" },
]

const PortfolioProjectModal = ({
  open,
  onClose,
  onCreateProject,
  initialCategory,
}: PortfolioProjectModalProps) => {
  const titleRef = useRef<InputRef>(null)
  const [title, setTitle] = useState("")
  const [category, setCategory] = useState<PortfolioMockProject["category"]>(
    initialCategory ?? "sustained"
  )
  const [coverImageUrl, setCoverImageUrl] = useState("")
  const [notes, setNotes] = useState("")
  const [dimensions, setDimensions] = useState("")
  const [materials, setMaterials] = useState("")
  const [processes, setProcesses] = useState("")
  const [ideas, setIdeas] = useState("")

  useEffect(() => {
    if (open && initialCategory) {
      setCategory(initialCategory)
    }
  }, [open, initialCategory])

  const resetAndClose = () => {
    setTitle("")
    setCategory(initialCategory ?? "sustained")
    setCoverImageUrl("")
    setNotes("")
    setDimensions("")
    setMaterials("")
    setProcesses("")
    setIdeas("")
    onClose()
  }

  return (
    <Modal
      title="Create Project"
      open={open}
      onCancel={resetAndClose}
      afterOpenChange={(visible) => {
        if (visible) titleRef.current?.focus()
      }}
      footer={[
        <Button key="cancel" onClick={resetAndClose}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          disabled={!title.trim()}
          onClick={() => {
            onCreateProject({
              title,
              category,
              coverImageUrl,
              notes,
              dimensions,
              materials,
              processes,
              ideas,
            })
            resetAndClose()
          }}
        >
          Create Project
        </Button>,
      ]}
    >
      <Flex col gap={10}>
        <Input
          ref={titleRef}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Project name (required)"
        />
        <Select
          options={categoryOptions}
          value={category}
          onChange={setCategory}
        />
        <Input
          value={coverImageUrl}
          onChange={(event) => setCoverImageUrl(event.target.value)}
          placeholder="Cover image URL (optional)"
        />
        <Input.TextArea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Notes"
          autoSize={{ minRows: 2, maxRows: 5 }}
        />
        <Input
          value={dimensions}
          onChange={(event) => setDimensions(event.target.value)}
          placeholder="Dimensions"
        />
        <Input
          value={materials}
          onChange={(event) => setMaterials(event.target.value)}
          placeholder="Materials"
        />
        <Input
          value={processes}
          onChange={(event) => setProcesses(event.target.value)}
          placeholder="Processes"
        />
        <Input
          value={ideas}
          onChange={(event) => setIdeas(event.target.value)}
          placeholder="Ideas"
        />
      </Flex>
    </Modal>
  )
}

export default PortfolioProjectModal
